import React, { useEffect, useState } from 'react';
import {
	Drawer,
	Form,
	Space,
	Switch,
	Table,
	Tag,
	Typography,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/pro-thin-svg-icons';
import { formatCurrency } from '@shared/utils/currency';
import { Currencies, CurrencySymbols, getPortActionTypeLabel, PortActionTypes } from '@shared/utils/constants';
import { formatNumber } from '@shared/utils/formatNumber';
import { Values } from '@shared/utils/objectEnums';
import { asyncMap } from '@shared/utils/array';
import { round } from '@shared/utils/math';
import { getIceClassEuEtsTotals, ICE_CLASS_EU_ETS_DISCOUNT } from '@shared/utils/euCompliance/iceClassDiscount';
import { GetVoyageExpensesResponse } from '@api/features/invoices/getVoyageExpenses';
import { EuEtsRow } from '@api/features/ops/utils/emissionsCompliance/emissionsComplianceTypes';
import Button from '@client/components/Button';
import { refreshVoyageEuaPrice, updateVoyageExpense } from '@client/lib/api';
import useFileUpload, { Upload } from '@client/utils/hooks/useFileUpload';
import getFileFromAttachment from '@client/utils/getFileFromAttachment';
import LoadingIndicator from '@client/components/LoadingIndicator';
import NumericInput from '@client/components/NumericInput';
import EstimatedActualTag from '@client/components/EstimatedActualTag';
import CurrencyInput from '@client/components/CurrencyInput';
import InvoiceAttachment from '../InvoiceAttachment';
import styles from './EuEtsExpenseDrawer.module.css';

type Props = {
	open: boolean;
	onClose: () => void;
	expenseData?: Partial<GetVoyageExpensesResponse['rows'][number]> | null;
	isIceClass: boolean;
};

type SavedSnapshot = {
	isIceClass: boolean;
	overrideAmount: number | null;
	euaActualPrice: number | null;
	euaExchangeRate: number | null;
	euaMarketPrice: number | null;
	rows: EuEtsRow[];
	attachments: Array<{
		name: string;
		attachmentId?: number;
	}>;
};

type UploadedAttachmentFile = File & {
	uid: string;
	attachmentId?: number;
};

const serializeSnapshot = (snapshot: SavedSnapshot) => JSON.stringify(snapshot);

const renderConsumptionSource = (
	source?: 'actual' | 'estimated',
) => {
	if (source == null) {
		return '—';
	}

	return (
		<EstimatedActualTag
			estimated={source === 'estimated'}
		/>
	);
};

const EuEtsExpenseDrawer = ({
	open,
	onClose,
	expenseData,
	isIceClass,
}: Props) => {
	const [loadingAttachments, setLoadingAttachments] = useState(false);
	const {
		uploaded,
		uploadProps,
		onRemoveUpload,
		onSetUploaded,
		onResetUploaded,
	} = useFileUpload({ multiple: true });

	const hasBreakdown = (expenseData?.euCalculationRows?.length ?? 0) > 0;
	const [euaActualPrice, setEuaActualPrice] = useState<number | null>(expenseData?.euaActualPrice ?? null);
	const [euaExchangeRate, setEuaExchangeRate] = useState<number | null>(expenseData?.euaExchangeRate ?? null);
	const [euaMarketPrice, setEuaMarketPrice] = useState<number | null>(expenseData?.euaMarketPrice ?? null);
	const [overrideAmount, setOverrideAmount] = useState<number | null>(
		expenseData?.overrideAmount == null ?
			null :
			Number(expenseData.overrideAmount),
	);

	const currency = expenseData?.currency ?? Currencies.USD;

	const [saving, setSaving] = useState(false);
	const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
	const [refreshing, setRefreshing] = useState(false);
	const [localRows, setLocalRows] = useState<EuEtsRow[]>([]);
	const [appliedIceClass, setAppliedIceClass] = useState(expenseData?.isIceClass ?? false);
	
	const isValidPositiveNumber = (
		value: number | null | undefined,
	): value is number => (
		value != null &&
		Number.isFinite(value) &&
		value > 0
	);

	const marketPriceInContractCurrency =
		isValidPositiveNumber(euaMarketPrice) &&
		isValidPositiveNumber(euaExchangeRate) ?
			euaMarketPrice * euaExchangeRate :
			null;

	const contractPricePerEua =
		isValidPositiveNumber(euaActualPrice) ?
			euaActualPrice :
			marketPriceInContractCurrency;

	const storedIceClassDiscount = expenseData?.isIceClass ? 1 - ICE_CLASS_EU_ETS_DISCOUNT : 1;

	const storedRows = (expenseData?.euCalculationRows ?? []) as EuEtsRow[];

	const storedTotalEuas = storedRows.reduce(
		(sum, row) => sum + row.euasRequired,
		0,
	);

	const storedContractPricePerEua =
		storedTotalEuas > 0 ?
			Math.abs(Number(expenseData?.amount ?? 0)) / (storedTotalEuas * storedIceClassDiscount) :
			0;

	const hasResolvableEuaPrice = contractPricePerEua != null || storedContractPricePerEua > 0;

	const calculatedRows = localRows.map((row) => {
		const euasRequired = row.apply ?
			row.totalCo2 *
			(row.qualifyingPercent / 100) *
			(row.phasePercent / 100) :
			0;
		const amount = euasRequired * (
			contractPricePerEua ??
			storedContractPricePerEua
		);

		return {
			...row,
			euasRequired,
			amount,
			contractAmount: amount,
		};
	});

	const totalCo2 = calculatedRows.reduce(
		(sum, row) => sum + (row.apply ? row.totalCo2 : 0),
		0,
	);

	const totalEuas = calculatedRows.reduce(
		(sum, row) => sum + row.euasRequired,
		0,
	);

	const {
		subTotal: subTotalAmount,
		discount: iceClassDiscountAmount,
		total: totalAmount,
	} = getIceClassEuEtsTotals(
		calculatedRows.reduce(
			(sum, row) => sum + row.amount,
			0,
		),
		appliedIceClass,
	);

	useEffect(() => {
		const initialMarketPrice = expenseData?.euaMarketPrice ?? null;
		const initialExchangeRate = expenseData?.euaExchangeRate ?? null;
		const initialActualPrice = expenseData?.euaActualPrice ?? null;

		const initialMarketPriceInContractCurrency =
			isValidPositiveNumber(initialMarketPrice) &&
		isValidPositiveNumber(initialExchangeRate) ?
				initialMarketPrice * initialExchangeRate :
				null;

		const initialContractPricePerEua =
			isValidPositiveNumber(initialActualPrice) ?
				initialActualPrice :
				initialMarketPriceInContractCurrency;

		const initialStoredRows =
			(expenseData?.euCalculationRows ?? []) as EuEtsRow[];

		const initialStoredTotalEuas = initialStoredRows.reduce(
			(sum, row) => sum + row.euasRequired,
			0,
		);

		const initialStoredContractPricePerEua =
			initialStoredTotalEuas > 0 ?
				Math.abs(Number(expenseData?.amount ?? 0)) /
				(initialStoredTotalEuas * (expenseData?.isIceClass ? 1 - ICE_CLASS_EU_ETS_DISCOUNT : 1)) :
				0;

		const initialRows = initialStoredRows.map((row) => {
			const euasRequired = row.apply ?
				row.totalCo2 *
			(row.qualifyingPercent / 100) *
			(row.phasePercent / 100) :
				0;

			const amount = euasRequired * (
				initialContractPricePerEua ??
			initialStoredContractPricePerEua
			);

			return {
				...row,
				euasRequired,
				amount,
				contractAmount: amount,
			};
		});

		setSavedSnapshot(
			serializeSnapshot({
				isIceClass: expenseData?.isIceClass ?? false,
				overrideAmount:
				expenseData?.overrideAmount == null ?
					null :
					Number(expenseData.overrideAmount),
				euaActualPrice: initialActualPrice,
				euaExchangeRate: initialExchangeRate,
				euaMarketPrice: initialMarketPrice,
				rows: initialRows,
				attachments: (expenseData?.attachments ?? []).map((attachment) => ({
					name: attachment.name,
					attachmentId: attachment.id,
				})),
			}),
		);
	}, [
		expenseData?.id,
		expenseData?.overrideAmount,
		expenseData?.euaActualPrice,
		expenseData?.euaExchangeRate,
		expenseData?.euaMarketPrice,
		expenseData?.euCalculationRows,
		expenseData?.attachments,
		expenseData?.amount,
		expenseData?.isIceClass,
	]);

	useEffect(() => {
		setOverrideAmount(
			expenseData?.overrideAmount == null ?
				null :
				Number(expenseData.overrideAmount),
		);
	}, [expenseData?.id, expenseData?.overrideAmount]);

	useEffect(() => {
		setLocalRows((expenseData?.euCalculationRows ?? []) as EuEtsRow[]);
	}, [expenseData?.id, expenseData?.euCalculationRows]);

	useEffect(() => {
		setAppliedIceClass(expenseData?.isIceClass ?? false);
	}, [expenseData?.id, expenseData?.isIceClass]);

	useEffect(() => {
		setEuaMarketPrice(expenseData?.euaMarketPrice ?? null);
		setEuaActualPrice(expenseData?.euaActualPrice ?? null);
		setEuaExchangeRate(expenseData?.euaExchangeRate ?? null);
	}, [
		expenseData?.id,
		expenseData?.euaMarketPrice,
		expenseData?.euaActualPrice,
		expenseData?.euaExchangeRate,
	]);

	useEffect(() => {
		const updateDefaultFiles = async () => {
			setLoadingAttachments(true);
			if (expenseData == null || expenseData.attachments?.length === 0) {
				setLoadingAttachments(false);

				return;
			}

			const expenseAttachments = expenseData?.attachments ?? [];

			const expenseFiles = await asyncMap(
				expenseAttachments,
				async (attachment) => {
					const file: File & {
						uid?: string;
						attachmentId?: number;
						deleteable?: boolean;
					} = await getFileFromAttachment(attachment);
					file.uid = `${file.name}-${file.lastModified}`;
					file.attachmentId = attachment.id;

					return {
						file: file as File & {
							uid: string;
							attachmentId: number;
							deleteable?: boolean;
						},
						url: window.URL.createObjectURL(file),
						deleteable: true,
					};
				},

			);

			onSetUploaded(expenseFiles);
			setLoadingAttachments(false);
		};

		updateDefaultFiles();
	}, [expenseData, onSetUploaded]);

	useEffect(() => {
		if (!open) {
			onResetUploaded();
		}
	}, [open, onResetUploaded]);

	const uploadedFiles: Upload[] = [];

	if (Array.isArray(uploaded)) {
		uploadedFiles.push(...uploaded);
	} else if (uploaded != null) {
		uploadedFiles.push(uploaded);
	}

	const currentSnapshot = serializeSnapshot({
		isIceClass,
		overrideAmount,
		euaActualPrice,
		euaExchangeRate,
		euaMarketPrice,
		rows: calculatedRows,
		attachments: uploadedFiles.map((u) => {
			const file = u.file as UploadedAttachmentFile;

			return {
				name: file.name,
				attachmentId: file.attachmentId,
			};
		}),
	});

	const hasPendingChanges =
		!loadingAttachments &&
		savedSnapshot != null &&
		currentSnapshot !== savedSnapshot;

	const onRefreshMarketPrice = async () => {
		if (expenseData?.id == null) {
			return;
		}

		setRefreshing(true);

		try {
			const updatedExpense =
				await refreshVoyageEuaPrice(expenseData.id);

			const updatedMarketPrice =
				updatedExpense.euaMarketPrice ?? null;

			const updatedExchangeRate =
				updatedExpense.euaExchangeRate ?? null;

			setEuaMarketPrice(updatedMarketPrice);
			setEuaExchangeRate(updatedExchangeRate);
			setLocalRows(
				(updatedExpense.euCalculationRows ?? []) as EuEtsRow[],
			);
		} finally {
			setRefreshing(false);
		}
	};

	const onSave = async () => {
		if (
			expenseData?.id == null ||
		(
			overrideAmount == null &&
			hasBreakdown &&
			!hasResolvableEuaPrice
		)
		) {
			return;
		}

		setSaving(true);

		try {
			const amount =
				overrideAmount != null ?
					overrideAmount :
					totalAmount;

			await updateVoyageExpense(expenseData.id, {
				euaActualPrice,
				euaExchangeRate,
				euCalculationRows: calculatedRows,
				overrideAmount,
				isIceClass,
				amount: -Math.abs(amount),
				attachments: uploadedFiles.map((u) => u.file),
			});

			setSavedSnapshot(currentSnapshot);
			setAppliedIceClass(isIceClass);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Drawer
			open={open}
			onClose={onClose}
			title="EU ETS"
			extra={isIceClass && <Tag color="blue">Ice class</Tag>}
			width="70%"
			destroyOnClose
		>
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				<Form layout="vertical">
					<div className={styles.overrideFields}>
						<Form.Item label="Manual overwrite">
							<div className={styles.overrideRow}>
								<CurrencyInput
									data-testid="eu-ets-manual-overwrite"
									allowClear
									onClear={() => setOverrideAmount(null)}
									value={overrideAmount}
									onChange={(value) => {
										setOverrideAmount(
											value == null ?
												null :
												Number(value),
										);
									}}
								/>

								{overrideAmount == null && (
									<Typography.Text
										type="secondary"
										className={styles.overrideInfo}
									>
										You can manually overwrite the calculated cost here
									</Typography.Text>
								)}
							</div>
						</Form.Item>
					</div>

					<div className={styles.calculatedTotal}>
						<Form.Item label="Calculated total">
							<CurrencyInput
								value={Number(Math.abs(totalAmount).toFixed(2))}
								readOnly
								disabled={overrideAmount != null}
							/>
						</Form.Item>
					</div>

					{hasBreakdown && (
						<div className={styles.priceFields}>
							<Form.Item label="Market Index Price (EUR)">
								<NumericInput
									className={styles.marketPriceInput}
									value={
										euaMarketPrice == null ?
											null :
											round(euaMarketPrice, 2)
									}
									readOnly
									addonBefore={
										CurrencySymbols[
											Currencies.EUR as keyof typeof CurrencySymbols
										] ?? Currencies.EUR
									}
									addonAfter={(
										<Button
											type="text"
											className={styles.refreshButton}
											disabled={expenseData?.id == null}
											loading={refreshing}
											onClick={(e) => {
												e?.preventDefault();
												e?.stopPropagation();
												onRefreshMarketPrice();
											}}
											icon={<FontAwesomeIcon icon={faRefresh} />}
										/>
									)}
								/>
							</Form.Item>

							<Form.Item label={`Market Index Price (${currency})`}>
								<NumericInput
									className={styles.marketPriceInput}
									value={
										marketPriceInContractCurrency == null ?
											null :
											round(marketPriceInContractCurrency, 2)
									}
									readOnly
									addonBefore={
										CurrencySymbols[
											currency as keyof typeof CurrencySymbols
										] ?? currency
									}
								/>
							</Form.Item>

							<Form.Item label={`EUA actual price (${currency})`}>
								<NumericInput
									className={styles.actualPriceInput}
									value={euaActualPrice}
									addonBefore={
										CurrencySymbols[
											currency as keyof typeof CurrencySymbols
										] ?? currency
									}
									onChange={(value) => {
										setEuaActualPrice(
											value == null ?
												null :
												Number(value),
										);
									}}
								/>
							</Form.Item>
						</div>
					)}
				</Form>

				{!hasBreakdown ?
					(
						<Typography.Paragraph type="secondary">
							This contract was created before the EU ETS breakdown was introduced.
							Open the itinerary and recalculate emissions to generate the breakdown.
						</Typography.Paragraph>
					) :
					(
						<Table
							rowKey="key"
							dataSource={calculatedRows}
							pagination={false}
							size="small"
							columns={[
								{
									title: 'Port / Passage',
									dataIndex: 'label',
								},
								{
									title: 'Purpose',
									dataIndex: 'purpose',
									render: (purpose?: string) => {
										if (!purpose) {
											return '';
										}

										return purpose
											.split(', ')
											.map((p) => getPortActionTypeLabel(
												p as Values<typeof PortActionTypes>,
											))
											.join(', ');
									},
								},
								{
									title: 'Source',
									dataIndex: 'consumptionSource',
									key: 'consumptionSource',
									align: 'center',
									render: renderConsumptionSource,
								},
								{
									title: 'Total CO₂',
									dataIndex: 'totalCo2',
									align: 'right',
									render: (value: number) => formatNumber(value),
								},
								{
									title: 'Apply',
									dataIndex: 'apply',
									align: 'center',
									render: (_: boolean, row: EuEtsRow) => (
										<Switch
											disabled={
												row.qualifyingPercent === 0 ||
													row.totalCo2 === 0
											}
											checked={
												row.apply &&
													row.qualifyingPercent > 0 &&
													row.totalCo2 > 0
											}
											onChange={(checked) => {
												setLocalRows((prev) => prev.map((r) => (
													r.key === row.key ?
														{
															...r,
															apply: checked,
														} :
														r
												)));
											}}
										/>
									),
								},
								{
									title: 'Qualifying year',
									dataIndex: 'phase',
									align: 'center',
								},
								{
									title: 'Qualifying %',
									dataIndex: 'qualifyingPercent',
									align: 'center',
									render: (value?: number) => `${value ?? 0}%`,
								},
								{
									title: 'Requirement %',
									dataIndex: 'requirementPercent',
									align: 'center',
									render: (value?: number) => `${value ?? 0}%`,
								},
								{
									title: 'EUAs required',
									dataIndex: 'euasRequired',
									align: 'right',
									render: (value: number) => formatNumber(value),
								},
								{
									title: 'Contract amount',
									dataIndex: 'amount',
									align: 'right',
									render: (value: number) => (
										formatCurrency(Math.abs(value), currency)
									),
								},
							]}
							rowClassName={(row) => (
								row.totalCo2 === 0 || row.qualifyingPercent === 0 ?
									styles.disabledRow :
									''
							)}
							summary={() => (
								appliedIceClass ? (
									<>
										<Table.Summary.Row data-testid="eu-ets-sub-total">
											<Table.Summary.Cell index={0}>
												Sub-total
											</Table.Summary.Cell>
											<Table.Summary.Cell index={1} />
											<Table.Summary.Cell index={2} />
											<Table.Summary.Cell index={3} align="right">
												{formatNumber(totalCo2)}
											</Table.Summary.Cell>
											<Table.Summary.Cell index={4} />
											<Table.Summary.Cell index={5} />
											<Table.Summary.Cell index={6} />
											<Table.Summary.Cell index={7} />
											<Table.Summary.Cell index={8} align="right">
												{formatNumber(totalEuas)}
											</Table.Summary.Cell>
											<Table.Summary.Cell index={9} align="right">
												{formatCurrency(Math.abs(subTotalAmount), currency)}
											</Table.Summary.Cell>
										</Table.Summary.Row>
										<Table.Summary.Row data-testid="eu-ets-ice-class-discount">
											<Table.Summary.Cell index={0} colSpan={9}>
												Ice class - 5% discount
											</Table.Summary.Cell>
											<Table.Summary.Cell index={9} align="right">
												{formatCurrency(-Math.abs(iceClassDiscountAmount), currency)}
											</Table.Summary.Cell>
										</Table.Summary.Row>
										<Table.Summary.Row data-testid="eu-ets-total">
											<Table.Summary.Cell index={0} colSpan={9}>
												<strong>Total</strong>
											</Table.Summary.Cell>
											<Table.Summary.Cell index={9} align="right">
												<strong>
													{formatCurrency(Math.abs(totalAmount), currency)}
												</strong>
											</Table.Summary.Cell>
										</Table.Summary.Row>
									</>
								) : (
									<Table.Summary.Row data-testid="eu-ets-total">
										<Table.Summary.Cell index={0}>
											<strong>Total</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={1} />
										<Table.Summary.Cell index={2} />
										<Table.Summary.Cell index={3} align="right">
											<strong>{formatNumber(totalCo2)}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={4} />
										<Table.Summary.Cell index={5} />
										<Table.Summary.Cell index={6} />
										<Table.Summary.Cell index={7} />
										<Table.Summary.Cell index={8} align="right">
											<strong>{formatNumber(totalEuas)}</strong>
										</Table.Summary.Cell>
										<Table.Summary.Cell index={9} align="right">
											<strong>
												{formatCurrency(Math.abs(totalAmount), currency)}
											</strong>
										</Table.Summary.Cell>
									</Table.Summary.Row>
								)
							)}
						/>
					)
				}

				<Typography.Title level={5}>Attachments</Typography.Title>
				{loadingAttachments ? (
					<LoadingIndicator />
				) : (
					<div className={styles.attachmentsInner}>
						<InvoiceAttachment
							files={uploaded}
							uploadProps={uploadProps}
							onRemoveFile={onRemoveUpload}
						/>
					</div>
				)}
				<Space style={{ justifyContent: 'flex-end', width: '100%' }}>
					<Button onClick={onClose}>
						Close
					</Button>
					<Button
						type="primary"
						onClick={onSave}
						loading={saving}
						disabled={
							!hasPendingChanges ||
							(
								overrideAmount == null &&
								hasBreakdown &&
								!hasResolvableEuaPrice
							)
						}
					>
						Save Changes
					</Button>
				</Space>
			</Space>
		</Drawer >
	);
};

export default EuEtsExpenseDrawer;